import { Action, type UpcomingBirthday } from "@pilatestime/shared-types";
import { useSuspenseQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	Link,
	redirect,
	useNavigate,
} from "@tanstack/react-router";
import {
	AlertTriangle,
	ArrowRight,
	Cake,
	CalendarDays,
	Calendar as CalendarIcon,
	CreditCard,
	UserPlus,
	Users,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { fetchAccessProfileQueryOptions } from "@/api/accessProfile";
import { fetchAnalyticsDashboardQueryOptions } from "@/api/analytics";
import { fetchUserOrganizationsQueryOptions } from "@/api/organizations";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { can, useCan } from "@/hooks/useAccessControl";
import { useApiParameters } from "@/hooks/useApiParameters";
import { useDateFormatter } from "@/hooks/useDateFormatter";

export const Route = createFileRoute("/_auth/app/_app/org/$orgId/")({
	loader: async ({ context, params }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		const orgId = params.orgId;

		// Access profile is already cached by the parent layout loader
		const profile = await context.queryClient.ensureQueryData(
			fetchAccessProfileQueryOptions(userId, token, orgId),
		);

		// If user cannot view analytics (e.g. ORG_INSTRUCTOR_CALENDAR_ONLY),
		// redirect to the first page they can access
		if (!can(profile, Action.VIEW_ANALYTICS)) {
			if (can(profile, Action.VIEW_ATTENDANCE)) {
				throw redirect({ to: "/app/org/$orgId/attendance", params: { orgId } });
			}
			// Fallback: stay on page but don't fetch analytics
		}

		// Preload user organizations data
		context.queryClient.ensureQueryData(
			fetchUserOrganizationsQueryOptions(userId, token),
		);

		// Preload business analytics dashboard data
		context.queryClient.ensureQueryData(
			fetchAnalyticsDashboardQueryOptions(userId, token, orgId),
		);

		return { userId, token, orgId };
	},
	component: BusinessAnalytics,
});

// Stat Card Component
interface StatCardProps {
	title: string;
	value: string | number;
	icon: React.ReactNode;
	subtitle?: string;
	variant?: "primary" | "success" | "warning" | "error" | "info";
	onClick?: () => void;
}

function StatCard({
	title,
	value,
	icon,
	subtitle,
	variant = "primary",
	onClick,
}: StatCardProps) {
	const variantStyles = {
		primary: "from-primary/10 to-primary/5 border-primary/20",
		success: "from-success/10 to-success/5 border-success/20",
		warning: "from-warning/10 to-warning/5 border-warning/20",
		error: "from-error/10 to-error/5 border-error/20",
		info: "from-info/10 to-info/5 border-info/20",
	};

	const iconBgStyles = {
		primary: "bg-primary/10 text-primary",
		success: "bg-success/10 text-success",
		warning: "bg-warning/10 text-warning",
		error: "bg-error/10 text-error",
		info: "bg-info/10 text-info",
	};

	return (
		<Card
			className={`bg-linear-to-br ${variantStyles[variant]} border hover:shadow-lg transition-all duration-300 ${onClick ? "cursor-pointer" : ""}`}
			onClick={onClick}
		>
			<CardContent className="p-4 sm:p-6">
				<div className="flex items-start justify-between gap-3">
					<div className="flex-1 min-w-0">
						<p className="text-xs sm:text-sm font-medium text-base-content/60 wrap-break-words">
							{title}
						</p>
						<p className="mt-1 text-2xl sm:text-3xl font-bold text-base-content">
							{typeof value === "number" ? value.toLocaleString() : value}
						</p>
						{subtitle && (
							<p className="mt-1 text-xs text-base-content/50">{subtitle}</p>
						)}
					</div>
					<div
						className={`shrink-0 p-2 sm:p-3 rounded-xl ${iconBgStyles[variant]}`}
					>
						{icon}
					</div>
				</div>
			</CardContent>
		</Card>
	);
}

// Quick Action Card Component
interface QuickActionProps {
	title: string;
	description: string;
	icon: React.ReactNode;
	href: string;
	variant?: "primary" | "success" | "warning" | "info";
}

function QuickActionCard({
	title,
	description,
	icon,
	href,
	variant = "primary",
}: QuickActionProps) {
	const variantStyles = {
		primary: "hover:border-primary/40 group-hover:bg-primary/5",
		success: "hover:border-success/40 group-hover:bg-success/5",
		warning: "hover:border-warning/40 group-hover:bg-warning/5",
		info: "hover:border-info/40 group-hover:bg-info/5",
	};

	const iconStyles = {
		primary: "text-primary group-hover:scale-110",
		success: "text-success group-hover:scale-110",
		warning: "text-warning group-hover:scale-110",
		info: "text-info group-hover:scale-110",
	};

	return (
		<Link to={href} className="group block">
			<Card
				className={`h-full border-2 border-base-300 transition-all duration-300 ${variantStyles[variant]} hover:shadow-md`}
			>
				<CardContent className="p-4 sm:p-5">
					<div className="flex items-center gap-3 sm:gap-4">
						<div
							className={`transition-transform duration-300 ${iconStyles[variant]}`}
						>
							{icon}
						</div>
						<div className="flex-1 min-w-0">
							<h3 className="font-semibold text-base-content text-sm sm:text-base">
								{title}
							</h3>
							<p className="text-xs sm:text-sm text-base-content/60 line-clamp-2">
								{description}
							</p>
						</div>
						<ArrowRight className="h-4 w-4 sm:h-5 sm:w-5 text-base-content/30 group-hover:text-base-content/60 group-hover:translate-x-1 transition-all duration-300 shrink-0" />
					</div>
				</CardContent>
			</Card>
		</Link>
	);
}

// Birthday List Item Component
interface BirthdayItemProps {
	birthday: UpcomingBirthday;
}

function BirthdayItem({ birthday }: BirthdayItemProps) {
	const { locale } = useDateFormatter();

	const formatDayMonth = (dob: string) => {
		const [day, month] = dob.split("-");
		const currentYear = new Date().getFullYear();
		const date = new Date(
			currentYear,
			Number.parseInt(month, 10) - 1,
			Number.parseInt(day, 10),
		);
		return date.toLocaleDateString(locale, {
			day: "2-digit",
			month: "short",
		});
	};

	return (
		<div className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-lg bg-base-200/50">
			<div className="shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-warning/10 flex items-center justify-center">
				<Cake className="h-4 w-4 sm:h-5 sm:w-5 text-warning" />
			</div>
			<div className="flex-1 min-w-0">
				<p className="font-medium text-base-content text-sm sm:text-base wrap-break-words">
					{birthday.name}
				</p>
				<p className="text-xs text-base-content/60 wrap-break-words">
					{birthday.email}
				</p>
			</div>
			<Badge variant="warning" className="shrink-0 text-xs">
				{formatDayMonth(birthday.dateOfBirth)}
			</Badge>
		</div>
	);
}

function BusinessAnalytics() {
	const { t } = useTranslation();
	const { orgId } = Route.useParams();
	const { userId, token } = Route.useLoaderData();
	const navigate = useNavigate();

	// Get business analytics dashboard data
	const { data: analyticsData } = useSuspenseQuery(
		fetchAnalyticsDashboardQueryOptions(userId, token, orgId),
	);

	// Authorization check
	const can = useCan();
	const canManageEnrollments = can(Action.MANAGE_ENROLLMENTS);
	const canManageBilling = can(Action.MANAGE_PAYMENTS);

	const { businessMetrics, customerLifecycle, upcomingBirthdays } =
		analyticsData;

	// Navigation handler for expiring soon plans
	const handleExpiringSoonClick = () => {
		navigate({
			to: "/app/org/$orgId/enrollments",
			params: { orgId },
			search: {
				page: 1,
				limit: 20,
				search: "",
				expiringSoon: true,
				statuses: ["ACTIVE"],
			},
		});
	};

	return (
		<div className="h-full overflow-y-auto">
			<div className="max-w-7xl mx-auto space-y-6 sm:space-y-8 pb-6">
				{/* Business Overview Section */}
				<section>
					<h2 className="text-lg sm:text-xl font-semibold text-base-content mb-4">
						{t("dashboard.businessOverview.title")}
					</h2>
					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
						<StatCard
							title={t("dashboard.businessOverview.activeCustomers.title")}
							value={businessMetrics.activeCustomers}
							icon={<Users className="h-5 w-5 sm:h-6 sm:w-6" />}
							subtitle={t(
								"dashboard.businessOverview.activeCustomers.subtitle",
							)}
							onClick={() => {
								navigate({
									to: "/app/org/$orgId/customers",
									params: { orgId },
								});
							}}
							variant="primary"
						/>
						<StatCard
							title={t("dashboard.businessOverview.activePlans.title")}
							value={businessMetrics.activePlans}
							icon={<CreditCard className="h-5 w-5 sm:h-6 sm:w-6" />}
							subtitle={t("dashboard.businessOverview.activePlans.subtitle")}
							variant="success"
							onClick={() => {
								navigate({
									to: "/app/org/$orgId/enrollments",
									params: { orgId },
								});
							}}
						/>
						<StatCard
							title={t("dashboard.upcomingEvents.plansExpiringIn7Days.title")}
							value={customerLifecycle.plansExpiringIn7Days}
							icon={<CalendarIcon className="h-5 w-5 sm:h-6 sm:w-6" />}
							subtitle={t(
								"dashboard.upcomingEvents.plansExpiringIn7Days.subtitle",
							)}
							variant="warning"
							onClick={handleExpiringSoonClick}
						/>
						<StatCard
							title={t("dashboard.upcomingEvents.plansExpiredLast30Days.title")}
							value={customerLifecycle.plansExpiredLast30Days}
							icon={<AlertTriangle className="h-5 w-5 sm:h-6 sm:w-6" />}
							subtitle={t(
								"dashboard.upcomingEvents.plansExpiredLast30Days.subtitle",
							)}
							variant="error"
							onClick={() => {
								navigate({
									to: "/app/org/$orgId/enrollments",
									params: { orgId },
									search: {
										page: 1,
										limit: 20,
										statuses: ["EXPIRED"],
									},
								});
							}}
						/>
					</div>
				</section>

				<div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
					{/* Quick Actions */}
					<section className="lg:col-span-2">
						<h2 className="text-lg sm:text-xl font-semibold text-base-content mb-4 flex items-center gap-2">
							<div className="h-1.5 w-1.5 rounded-full bg-success" />
							{t("organization.quickActions")}
						</h2>
						<div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
							{/* My Schedules - always visible for all users */}
							<QuickActionCard
								title={t("dashboard.quickActions.mySchedules.title")}
								description={t(
									"dashboard.quickActions.mySchedules.description",
								)}
								icon={<CalendarDays className="h-6 w-6 sm:h-8 sm:w-8" />}
								href={`/app/org/${orgId}/attendance?instructor=${userId}`}
								variant="success"
							/>

							{/* Customers - always visible for all users */}
							<QuickActionCard
								title={t("navigation.customers")}
								description={t("dashboard.quickActions.customers")}
								icon={<Users className="h-6 w-6 sm:h-8 sm:w-8" />}
								href={`/app/org/${orgId}/customers`}
								variant="primary"
							/>

							{/* Create Enrollment - only visible for users with MANAGE_ENROLLMENTS permission */}
							{canManageEnrollments && (
								<QuickActionCard
									title={t("dashboard.quickActions.createEnrollment.title")}
									description={t(
										"dashboard.quickActions.createEnrollment.description",
									)}
									icon={<UserPlus className="h-6 w-6 sm:h-8 sm:w-8" />}
									href={`/app/org/${orgId}/enrollments/new`}
									variant="info"
								/>
							)}
							{/* Create Invoice - only visible for users with MANAGE_PAYMENTS permission */}
							{canManageBilling && (
								<QuickActionCard
									title={t("dashboard.quickActions.createInvoice.title")}
									description={t(
										"dashboard.quickActions.createInvoice.description",
									)}
									icon={<CreditCard className="h-6 w-6 sm:h-8 sm:w-8" />}
									href={`/app/org/${orgId}/billing/new`}
									variant="primary"
								/>
							)}
						</div>
					</section>

					{/* Upcoming Birthdays Section */}
					<section>
						<h2 className="text-lg sm:text-xl font-semibold text-base-content mb-4">
							{t("common.upcomingBirthdays")}
						</h2>
						<Card className="bg-base-100 border border-base-300">
							<CardContent className="p-4 sm:p-6">
								{upcomingBirthdays.length > 0 ? (
									<div className="space-y-3">
										{upcomingBirthdays.map((birthday) => (
											<BirthdayItem key={birthday.id} birthday={birthday} />
										))}
									</div>
								) : (
									<div className="text-center py-8 text-base-content/50">
										<Cake className="h-12 w-12 mx-auto mb-3 opacity-50" />
										<p>{t("common.noData")}</p>
									</div>
								)}
							</CardContent>
						</Card>
					</section>
				</div>
			</div>
		</div>
	);
}
